import * as React from 'react'
import { Row, Col, Button, FormGroup, Label } from 'reactstrap'

interface ContextBuilderProps {
  context: any
  onChange: (context: any) => void
  visible: boolean // Show only for Nunjucks templates
}

interface ContextBuilderState {
  contextValue: string
  validationErrors: string[]
  isValid: boolean
}

const exampleContexts = [
  {
    name: 'Article Analysis',
    context: {
      "article": {
        "title": "AI in Business: A Comprehensive Overview",
        "content": "Artificial intelligence is transforming the way businesses operate...",
        "author": "Tech Analyst",
        "publishDate": "2024-01-15"
      },
      "analysis": {
        "keyPoints": ["efficiency", "automation", "decision making"],
        "industry": "technology",
        "targetAudience": "business executives"
      }
    }
  },
  {
    name: 'Content Creation',
    context: {
      "brand": {
        "name": "TechCorp",
        "voice": "professional",
        "industry": "software"
      },
      "campaign": {
        "goal": "lead generation",
        "audience": "decision makers",
        "channels": ["email", "social media"]
      },
      "content": {
        "type": "blog post",
        "keywords": ["innovation", "efficiency", "ROI"],
        "wordCount": 800
      }
    }
  },
  {
    name: 'Customer Support',
    context: {
      "customer": {
        "tier": "premium",
        "industry": "healthcare",
        "issueHistory": ["billing", "technical"]
      },
      "issue": {
        "category": "technical",
        "severity": "medium",
        "description": "Integration API not responding"
      },
      "resolution": {
        "timeframe": "24 hours",
        "escalationLevel": 1
      }
    }
  }
]

export class ContextBuilder extends React.Component<ContextBuilderProps, ContextBuilderState> {
  constructor(props: ContextBuilderProps) {
    super(props)
    
    this.state = {
      contextValue: this.formatContext(props.context || {}),
      validationErrors: [],
      isValid: true
    }
  }

  componentDidUpdate(prevProps: ContextBuilderProps) {
    if (prevProps.context !== this.props.context) {
      this.setState({
        contextValue: this.formatContext(this.props.context || {}),
        validationErrors: [],
        isValid: true
      })
    }
  }

  private formatContext = (context: any): string => {
    try {
      return JSON.stringify(context, null, 2)
    } catch (e) {
      return '{}'
    }
  }

  private validateJSON = (value: string): string[] => {
    const errors: string[] = []
    
    if (!value || value.trim().length === 0) {
      return errors // Empty is valid
    }

    try {
      const parsed = JSON.parse(value)
      
      // Additional validation rules
      if (typeof parsed !== 'object' || parsed === null) {
        errors.push('Context must be a JSON object')
      }
      
      // Check for circular references
      try {
        JSON.stringify(parsed)
      } catch (e) {
        errors.push('Context contains circular references')
      }

    } catch (e) {
      if (e instanceof SyntaxError) {
        errors.push(`Invalid JSON syntax: ${e.message}`)
      } else {
        errors.push('Invalid JSON format')
      }
    }

    return errors
  }

  private handleContextChange = (value: string) => {
    const validationErrors = this.validateJSON(value)
    const isValid = validationErrors.length === 0

    this.setState({
      contextValue: value,
      validationErrors,
      isValid
    })

    // Parse and send valid JSON to parent
    if (isValid) {
      try {
        const parsedContext = value.trim().length === 0 ? {} : JSON.parse(value)
        this.props.onChange(parsedContext)
      } catch (e) {
        // This shouldn't happen if validation passed, but handle gracefully
        this.props.onChange({})
      }
    }
  }

  private loadExample = (example: any) => {
    const formattedContext = this.formatContext(example.context)
    this.handleContextChange(formattedContext)
  }

  private clearContext = () => {
    this.handleContextChange('{}')
  }

  private formatJSON = () => {
    try {
      const parsed = JSON.parse(this.state.contextValue)
      const formatted = JSON.stringify(parsed, null, 2)
      this.handleContextChange(formatted)
    } catch (e) {
      // Don't change value if it's invalid JSON
    }
  }

  private minifyJSON = () => {
    try {
      const parsed = JSON.parse(this.state.contextValue)
      const minified = JSON.stringify(parsed)
      this.handleContextChange(minified)
    } catch (e) {
      // Don't change value if it's invalid JSON
    }
  }

  render() {
    if (!this.props.visible) {
      return null
    }

    const { contextValue, validationErrors, isValid } = this.state

    return (
      <div className="context-builder">
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Context Builder (Nunjucks)</h6>
              <div>
                <Button 
                  size="sm" 
                  color="info" 
                  outline 
                  className="mr-2"
                  onClick={this.formatJSON}
                  disabled={!isValid}
                >
                  <i className="fa fa-indent"></i> Format
                </Button>
                <Button 
                  size="sm" 
                  color="info" 
                  outline 
                  className="mr-2"
                  onClick={this.minifyJSON}
                  disabled={!isValid}
                >
                  <i className="fa fa-compress"></i> Minify
                </Button>
                <Button 
                  size="sm" 
                  color="secondary" 
                  outline
                  onClick={this.clearContext}
                >
                  <i className="fa fa-refresh"></i> Clear
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <div className="context-info">
              <small className="text-info">
                <i className="fa fa-info-circle"></i> Context provides data for Nunjucks templates. 
                Use <code>{`{{ context.variable }}`}</code> in your templates to access these values.
              </small>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <FormGroup>
              <Label>JSON Context</Label>
              <textarea
                className={`form-control ${!isValid ? 'is-invalid' : ''}`}
                style={{
                  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                  fontSize: '14px',
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  resize: 'vertical',
                  minHeight: '200px',
                  lineHeight: '1.5'
                }}
                value={contextValue}
                onChange={(e) => this.handleContextChange(e.target.value)}
                placeholder={`{
  "variable": "value",
  "nested": {
    "property": "example"
  }
}`}
                spellCheck={false}
              />
              {validationErrors.length > 0 && (
                <div className="invalid-feedback d-block">
                  {validationErrors.map((error, index) => (
                    <div key={index}>
                      <i className="fa fa-exclamation-triangle"></i> {error}
                    </div>
                  ))}
                </div>
              )}
              {isValid && contextValue.trim().length > 0 && (
                <div className="valid-feedback d-block">
                  <i className="fa fa-check-circle"></i> Valid JSON context
                </div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="context-examples">
              <Label>Example Templates:</Label>
              <div className="btn-group-vertical w-100" role="group">
                {exampleContexts.map((example, index) => (
                  <Button
                    key={index}
                    size="sm"
                    color="light"
                    outline
                    className="text-left mb-1"
                    onClick={() => this.loadExample(example)}
                  >
                    <i className="fa fa-file-code-o"></i> {example.name}
                  </Button>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <div className={`alert ${isValid ? 'alert-success' : 'alert-warning'} py-2`}>
              <small>
                <i className={`fa ${isValid ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                {isValid 
                  ? ' Context is valid and ready for Nunjucks template processing'
                  : ' Please fix JSON errors before testing'
                }
              </small>
            </div>
          </Col>
        </Row>
      </div>
    )
  }
}